import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../services/api";
import PurchaseModal from "../components/public/PurchaseModal";
import PublicNavbar from "../components/public/PublicNavbar";
import PublicFooter from "../components/public/PublicFooter";
import HeroCarousel from "../components/public/HeroCarousel";
import VideoDemonstration from "../components/public/VideoDemonstration";
import { buildWhatsAppUrl } from "../utils/whatsapp";
import { DEFAULT_LUMIGO_PRODUCTS } from "../data/defaultProducts";
import {
    FaArrowRight, FaWhatsapp, FaCheckCircle,
    FaLightbulb, FaDraftingCompass, FaSun, FaTools, FaTruck,
    FaSearch
} from "react-icons/fa";

const Home = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState(DEFAULT_LUMIGO_PRODUCTS);
    const [siteContent, setSiteContent] = useState(null);
    const [showInquiry, setShowInquiry] = useState(false);
    const [selectedProductForInquiry, setSelectedProductForInquiry] = useState(null);
    const [productSearch, setProductSearch] = useState("");

    // Load products and dynamic site content
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const [prodRes, contentRes] = await Promise.allSettled([
                    api.get("/api/products"),
                    api.get("/api/content"),
                ]);

                if (isMounted) {
                    if (prodRes.status === "fulfilled" && Array.isArray(prodRes.value?.data) && prodRes.value.data.length > 0) {
                        setProducts(prodRes.value.data);
                    } else {
                        setProducts(DEFAULT_LUMIGO_PRODUCTS);
                    }
                    if (contentRes.status === "fulfilled" && contentRes.value?.data) {
                        setSiteContent(contentRes.value.data);
                    }
                }
            } catch (e) {
                console.warn("Failed to fetch initial home data:", e);
                if (isMounted) {
                    setProducts(DEFAULT_LUMIGO_PRODUCTS);
                }
            }
        };

        fetchData();
        return () => { isMounted = false; };
    }, []);

    const openInquiryModal = (product = null) => {
        setSelectedProductForInquiry(product);
        setShowInquiry(true);
    };

    const applications = [
        {
            title: "Industrial & Warehouses",
            image: "/assets/lumigo/projector-4.jpg",
            tag: "Safety & Logistics",
            desc: "Continuous optical safety walkways, forklift crossing warnings, and clear hazard stop signs.",
        },
        {
            title: "Hotels & Restaurants",
            image: "/assets/lumigo/projector-6.jpg",
            tag: "Facade & Entrance",
            desc: "High-contrast architectural logo projection greeting guests at driveways and entry portals.",
        },
        {
            title: "Retail & Showrooms",
            image: "/assets/lumigo/projector-14.jpg",
            tag: "Rangoli & Displays",
            desc: "Vibrant floor branding, seasonal promotional projections, and eye-catching rotating emblems.",
        },
        {
            title: "Weddings & Events",
            image: "/assets/lumigo/projector-12.jpg",
            tag: "Monograms & Galas",
            desc: "Custom high-resolution glass monogram gobos casting pristine wedding emblems and sponsor logos.",
        },
        {
            title: "Safety & Signage",
            image: "/assets/lumigo/projector-16.jpg",
            tag: "Floor Markings",
            desc: "Indestructible light markers replacing peeling epoxy paint and floor tape across active work floors.",
        },
    ];

    const vis = siteContent?.sectionVisibility || {};
    const activeProductList = (products && products.length > 0) ? products : DEFAULT_LUMIGO_PRODUCTS;

    return (
        <div className="min-h-screen bg-white text-[#263746] font-sans antialiased selection:bg-[#E3B629] selection:text-[#263746]">
            {/* Top Navigation */}
            <PublicNavbar onOpenEnquiry={() => openInquiryModal()} />

            {/* 1. HERO CAROUSEL SECTION */}
            {vis.heroCarousel !== false && (
                <HeroCarousel
                    slides={siteContent?.heroSlides}
                    carouselConfig={siteContent?.carouselConfig}
                    onOpenEnquiry={() => openInquiryModal()}
                />
            )}

            {/* 2. FIVE-CARD SERVICE BENEFIT STRIP */}
            {vis.benefitsStrip !== false && (
                <section className="bg-[#FFFFFF] border-b border-[#E5E7EB] py-8">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
                            {[
                                { icon: FaLightbulb, label: "High Brightness LED Projectors" },
                                { icon: FaDraftingCompass, label: "Custom Logo Gobo Designs" },
                                { icon: FaSun, label: "Indoor & Outdoor Use" },
                                { icon: FaTools, label: "Easy Installation" },
                                { icon: FaTruck, label: "PAN India Delivery" },
                            ].map((item, idx) => (
                                <div
                                    key={idx}
                                    className={`flex flex-col sm:flex-row items-center text-center sm:text-left gap-2.5 sm:gap-3 p-3.5 sm:p-4 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB]/80 hover:border-[#E3B629]/50 transition group ${idx === 4 ? "col-span-2 sm:col-span-1" : ""}`}
                                >
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-sm sm:text-base flex-shrink-0 group-hover:scale-110 transition-transform shadow-xs">
                                        <item.icon />
                                    </div>
                                    <span className="text-[11px] sm:text-xs font-bold text-[#263746] leading-tight font-heading">
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 3. FEATURED PRODUCT HIGHLIGHT: DXAX-150T */}
            {vis.featuredProduct !== false && (
                <section className="py-10 sm:py-14 bg-[#F7F5EF] border-b border-[#E5E7EB]">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-12 border border-[#E5E7EB] shadow-sm relative overflow-hidden">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                                {/* Left Information */}
                                <div className="lg:col-span-7 space-y-4 sm:space-y-5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[#E3B629] text-xs font-extrabold uppercase tracking-[0.25em] font-heading">
                                            FLAGSHIP HARDWARE
                                        </span>
                                        <span className="bg-[#FFF7E0] text-[#92400E] text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-[#E3B629]/30">
                                            Commercial Grade
                                        </span>
                                    </div>

                                    <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-[#263746] tracking-tight leading-tight font-heading">
                                        DXAX-150T Outdoor Gobo Projector (150W)
                                    </h2>
                                    <p className="text-[#687482] text-xs sm:text-sm leading-relaxed max-w-xl">
                                        Industrial-strength IP65 waterproof commercial gobo projector powered by high-output German OSRAM LED technology. Delivers razor-sharp projection across long architectural distances.
                                    </p>

                                    {/* 2x2 Specs Bullets */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 pt-2 text-xs font-bold text-[#263746]">
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] text-sm flex-shrink-0" />
                                            <span>3 – 25 Metres Projection Distance</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] text-sm flex-shrink-0" />
                                            <span>IP65 Weatherproof Aluminum Casing</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] text-sm flex-shrink-0" />
                                            <span>OSRAM High-Lumen Optical Engine</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] text-sm flex-shrink-0" />
                                            <span>Precision Glass Gobo Compatible</span>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4 pt-3 sm:pt-4">
                                        <button
                                            onClick={() => {
                                                const refProd = activeProductList.find(p => p.modelNumber === "DXAX-150T") || activeProductList[0];
                                                navigate(refProd ? `/product/${refProd._id}` : "/products");
                                            }}
                                            className="w-full sm:w-auto justify-center bg-[#E3B629] hover:bg-[#cfa320] text-[#263746] font-bold text-xs px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg flex items-center gap-2 uppercase tracking-wider transition shadow-sm font-heading text-center"
                                        >
                                            View Specifications <FaArrowRight className="text-[10px]" />
                                        </button>
                                        <button
                                            onClick={() => openInquiryModal({
                                                name: "DXAX-150T Outdoor Gobo Projector (150W)",
                                                modelNumber: "DXAX-150T",
                                                price: 32500,
                                            })}
                                            className="w-full sm:w-auto bg-white border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746] font-bold text-xs px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg uppercase tracking-wider transition font-heading text-center"
                                        >
                                            Request Formal Quote
                                        </button>
                                        <a
                                            href={buildWhatsAppUrl("Hello LUMIGO team, I would like to request technical specifications and quote for DXAX-150T Outdoor Gobo Projector.")}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full sm:w-auto justify-center bg-[#168B53] hover:bg-[#127244] text-white font-bold text-xs px-5 py-3 sm:py-3.5 rounded-lg flex items-center gap-2 uppercase tracking-wider transition font-heading text-center"
                                        >
                                            <FaWhatsapp className="text-sm" /> WhatsApp
                                        </a>
                                    </div>
                                </div>

                                {/* Right Projector Graphic */}
                                <div className="lg:col-span-5 flex justify-center">
                                    <div className="relative aspect-square w-full max-w-xs sm:max-w-sm flex items-center justify-center">
                                        {/* Circular Golden Glow Accent */}
                                        <div className="absolute inset-0 rounded-full bg-[#E3B629]/15 blur-2xl transform scale-90" />
                                        <div className="absolute inset-4 rounded-full border border-[#E3B629]/30" />

                                        <img
                                            src="/assets/lumigo/projector-2.jpg"
                                            alt="DXAX-150T Outdoor Gobo Projector"
                                            className="relative z-10 max-h-56 sm:max-h-72 object-contain drop-shadow-xl"
                                            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 4. POPULAR APPLICATIONS */}
            {vis.applications !== false && (
                <section className="py-12 sm:py-16 bg-white">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
                            <div>
                                <span className="text-[#E3B629] text-xs font-bold uppercase tracking-[0.2em] block mb-1 font-heading">
                                    Versatile Deployments
                                </span>
                                <h2 className="text-xl sm:text-3xl font-extrabold text-[#263746] tracking-tight font-heading">
                                    Projection Applications
                                </h2>
                            </div>
                            <Link
                                to="/applications"
                                className="text-xs font-bold text-[#E3B629] hover:text-[#cfa320] flex items-center gap-1 uppercase tracking-wider transition font-heading"
                            >
                                Explore All Applications <FaArrowRight className="text-[10px]" />
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
                            {applications.map((app, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => navigate("/applications")}
                                    className="group cursor-pointer rounded-2xl overflow-hidden border border-[#E5E7EB] hover:border-[#E3B629] hover:shadow-xl transition-all duration-300 flex flex-col bg-white"
                                >
                                    <div className="relative aspect-square w-full bg-[#141B22] overflow-hidden">
                                        <img
                                            src={app.image}
                                            alt={app.title}
                                            className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
                                            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                        <span className="absolute bottom-3 left-3 bg-[#E3B629] text-[#263746] text-[10px] font-black uppercase px-2 py-0.5 rounded shadow-xs font-heading">
                                            {app.tag}
                                        </span>
                                    </div>
                                    <div className="p-4 bg-white flex flex-col justify-between flex-grow">
                                        <div>
                                            <h3 className="text-xs font-bold text-[#263746] group-hover:text-[#E3B629] transition-colors leading-snug font-heading mb-1">
                                                {app.title}
                                            </h3>
                                            <p className="text-[11px] text-[#687482] line-clamp-2 leading-relaxed">
                                                {app.desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 5. FEATURED PRODUCT CATALOGUE GRID WITH INTEGRATED SEARCH */}
            {vis.productCatalogue !== false && (
                <section className="py-16 bg-[#F7F5EF] border-y border-[#E5E7EB]">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                            <div>
                                <span className="text-[#E3B629] text-xs font-bold uppercase tracking-[0.2em] block mb-1 font-heading">
                                    Commercial Hardware Lineup
                                </span>
                                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#263746] tracking-tight font-heading">
                                    Featured Projector Models
                                </h2>
                            </div>

                            {/* Relocated Search Bar in Product Section */}
                            <div className="flex items-center gap-3 w-full md:w-auto">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (productSearch.trim()) {
                                            navigate(`/products?search=${encodeURIComponent(productSearch.trim())}`);
                                        }
                                    }}
                                    className="relative flex-1 md:w-80"
                                >
                                    <input
                                        type="text"
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        placeholder="Search models (e.g. 150T, OSRAM, 200W)..."
                                        className="w-full bg-white border border-[#E5E7EB] rounded-full pl-10 pr-4 py-2.5 text-xs text-[#263746] placeholder-[#687482] focus:outline-none focus:border-[#E3B629] shadow-xs"
                                    />
                                    <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#687482] text-xs pointer-events-none" />
                                </form>

                                <Link
                                    to="/products"
                                    className="text-xs font-bold text-[#E3B629] hover:text-[#cfa320] flex items-center gap-1 uppercase tracking-wider transition font-heading whitespace-nowrap hidden sm:flex"
                                >
                                    All Hardware <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>

                        {/* Product Grid with live search filtering */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {(productSearch.trim()
                                ? activeProductList.filter(p =>
                                    p.name?.toLowerCase().includes(productSearch.toLowerCase().trim()) ||
                                    p.modelNumber?.toLowerCase().includes(productSearch.toLowerCase().trim()) ||
                                    p.description?.toLowerCase().includes(productSearch.toLowerCase().trim())
                                )
                                : activeProductList
                            ).slice(0, 6).map((product) => {
                                const specs = product?.specifications || {};
                                const power = specs.ratedPower || "150W";
                                const throwDist = specs.projectionDistance || "3 - 25 Metres";
                                const ip = specs.protectionRating || "IP65";

                                return (
                                    <div
                                        key={product._id}
                                        className="bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#E3B629] hover:shadow-xl transition-all duration-300 p-5 flex flex-col justify-between"
                                    >
                                        <div>
                                            <div className="relative aspect-[4/3] w-full bg-[#FFFFFF] rounded-xl overflow-hidden flex items-center justify-center p-4 mb-3 border border-[#E5E7EB]/50">
                                                <img
                                                    src={product.images?.[0] || product.image || "/assets/lumigo/projector-1.jpg"}
                                                    alt={product.name}
                                                    className="max-h-full max-w-full object-contain"
                                                    onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                                />
                                                <span className="absolute top-2 left-2 bg-[#FFF7E0] text-[#92400E] border border-[#E3B629]/40 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase font-heading">
                                                    {product.modelNumber || "Best Seller"}
                                                </span>
                                            </div>

                                            <Link to={`/product/${product._id}`}>
                                                <h3 className="text-sm font-bold text-[#263746] leading-snug line-clamp-2 hover:text-[#E3B629] transition-colors mb-2 font-heading">
                                                    {product.name}
                                                </h3>
                                            </Link>

                                            <div className="space-y-1 mb-4 text-xs text-[#687482]">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                                                    <span>{throwDist}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                                                    <span>{power} OSRAM Engine</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                                                    <span>{ip} Weatherproof</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-baseline gap-2 mb-3">
                                                <span className="text-base font-extrabold text-[#263746] font-mono">
                                                    ₹{(product.discountPrice || product.price || 32500).toLocaleString()}
                                                </span>
                                                {product.price > (product.discountPrice || 0) && (
                                                    <span className="text-xs text-[#9CA3AF] line-through font-mono">
                                                        ₹{product.price.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <Link
                                                    to={`/product/${product._id}`}
                                                    className="bg-[#E3B629] hover:bg-[#cfa320] text-[#263746] font-bold text-xs py-2.5 rounded-lg text-center transition font-heading"
                                                >
                                                    View Details
                                                </Link>
                                                <button
                                                    onClick={() => openInquiryModal(product)}
                                                    className="border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746] font-semibold text-xs py-2.5 rounded-lg text-center transition font-heading"
                                                >
                                                    Enquire Now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* 6. AUTO-PLAYING VIDEO DEMONSTRATION SECTION */}
            {vis.videoSection !== false && (
                <VideoDemonstration videoSection={siteContent?.videoSection} />
            )}

            {/* 7. CUSTOM GOBO FABRICATION SECTION */}
            {vis.customGoboCta !== false && (
                <section className="py-12 sm:py-16 bg-[#F7F5EF] border-y border-[#E5E7EB]">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-[#141B22] text-white rounded-3xl p-6 sm:p-10 lg:p-14 relative overflow-hidden shadow-xl">
                            {/* Decorative Gold Accent Backdrop */}
                            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#E3B629]/15 rounded-full blur-3xl" />
                            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#E3B629]/10 rounded-full blur-3xl" />

                            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
                                <div className="lg:col-span-8 space-y-3 sm:space-y-4">
                                    <div className="inline-flex items-center gap-2 bg-[#E3B629]/20 text-[#E3B629] text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] px-3 py-1 rounded-full border border-[#E3B629]/30 font-heading">
                                        <FaDraftingCompass /> CUSTOM GOBOS IN 24–48 HOURS
                                    </div>
                                    <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight font-heading">
                                        Bring Your Custom Brand to Light
                                    </h2>
                                    <p className="text-gray-300 text-xs sm:text-sm lg:text-base leading-relaxed max-w-2xl font-normal">
                                        Send us your vector logo, symbol, or artwork. Our optical engineers produce heat-resistant monochrome and full-colour dichroic glass gobos tailored precisely to your LUMIGO hardware.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 pt-2 text-xs text-gray-200">
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] flex-shrink-0" />
                                            <span>Vector PDF / AI / CDR format</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] flex-shrink-0" />
                                            <span>Full-colour dichroic coating</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="text-[#E3B629] flex-shrink-0" />
                                            <span>Fast dispatch across India</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-4 flex flex-col gap-2.5 sm:gap-3 justify-center">
                                    <button
                                        onClick={() => openInquiryModal({
                                            name: "Custom Gobo Optical Fabrication",
                                            modelNumber: "CUSTOM-GOBO",
                                            price: 2500,
                                        })}
                                        className="w-full bg-[#E3B629] hover:bg-[#cfa320] text-[#263746] font-extrabold text-xs py-3.5 sm:py-4 px-5 sm:px-6 rounded-xl text-center uppercase tracking-wider transition shadow-lg font-heading"
                                    >
                                        Order Custom Gobo
                                    </button>
                                    <a
                                        href={buildWhatsAppUrl("Hello LUMIGO team, I have a logo artwork file and would like to order a custom glass gobo.")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full bg-[#168B53] hover:bg-[#127244] text-white font-bold text-xs py-3.5 sm:py-4 px-5 sm:px-6 rounded-xl flex items-center justify-center gap-2 uppercase tracking-wider transition shadow-lg font-heading"
                                    >
                                        <FaWhatsapp className="text-base" /> WhatsApp Design Review
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 8. FOOTER WITH LOGO & PROPER ALIGNMENT */}
            <PublicFooter />

            {/* Direct Purchase & Instant Client Registration Modal */}
            {showInquiry && (
                <PurchaseModal
                    product={selectedProductForInquiry}
                    onClose={() => {
                        setShowInquiry(false);
                        setSelectedProductForInquiry(null);
                    }}
                />
            )}
        </div>
    );
};

export default Home;
